import type { IPitcherClient } from "@codesandbox/pitcher-client";
import { match } from "@codesandbox/states";
import type { PickState } from "@codesandbox/states";
import { useRegisterConfigFilesCommands } from "commands/registrations/useRegisterConfigFilesCommands";
import { useRegisterIntegrationCommands } from "commands/registrations/useRegisterIntegrationCommands";
import { CodeiumIntegration } from "components/Codeium/CodeiumIntegration";
import { EditorStoreProvider } from "components/Editor/useEditorStore";
import type { LiveSession } from "components/Editor/useEditorStore/live-session/queries";
import type { KnownErrors } from "components/Error/GlobalError";
import { GlobalError } from "components/Error/GlobalError";
import { GlobalNotifications } from "components/GlobalNotifications/GlobalNotifications";
import { useConvertToRepositoryDialog } from "components/Sandbox/ConvertToRepository/ConvertToRepositoryDialog";
import { ProjectSettingsModal } from "components/Settings/SettingsModal";
import { SettingsProvider } from "components/Settings/SettingsProvider";
import { useEnvironmentInterface } from "environment-interface";
import { env, setDevPitcherManagerURL } from "environment-interface/env";
import type {
  RemoteOpts,
  VSCodeRemoteLoadProgress,
} from "environment-interface/monacoEditor/browser/services/editor/vscode/remote";
import { useBranch } from "features/branch";
import {
  ContainerConsumer,
  ContainerProvider,
  useContainer,
} from "features/container/Feature";
import type { State as ContainerState } from "features/container/types";
import { useEditorFeature } from "features/editor/Feature";
import { useForkSandbox } from "features/forkSandbox";
import { MonacoLibraryFeature } from "features/monacoLibrary/Feature";
import {
  useAddAlertNotification,
  useAddStatusNotification,
} from "features/notifications/hooks";
import type { Editor as TEditor } from "features/types/editor";
import { usePitcher } from "features/utils/usePitcher";
import { AnimatePresence } from "framer-motion";
import { useCsbApi } from "hooks/api/useCsbApi";
import { useSession } from "hooks/api/useSession";
import { useFollowChannel } from "hooks/pitcher/useFollowChannel";
import { usePitcherNotifications } from "hooks/pitcher/usePitcherNotifications";
import { withBasePath } from "isomorphic/utils";
import { useControls, button } from "leva";
import { Box, Button, useTheme } from "prism-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { CancellationToken } from "vscode";
import { CancellationTokenSource } from "vscode";

import { AutoOpenTestRunnerDevtool } from "components/AutoOpenTestRunnerDevtool";
import { ErrorBoundary } from "components/ErrorBoundary";
import { GlobalModalProvider } from "components/GlobalModal";
import { MissingEnvVarNotification } from "components/MissingEnvVarNotification";
import { useActiveFile } from "hooks/useActiveFile";
import { useAnalytics } from "hooks/useAnalytics";
import { useBranchingAnimation } from "hooks/useBranchingAnimation";
import { useEditorRouter } from "hooks/useEditorRouter";
import { useVSCodeExtensionInitializedEffect } from "hooks/useVSCodeExtensionInitializedEffect";
import { canSeamlessBranch } from "utils/canSeamlessBranch";
import { getExperiments } from "utils/experiments";
import { safeLocalStorage } from "utils/safeLocalStorage";

import { BlockingLoader } from "../BlockingLoader";

import { ContainerError } from "./ContainerError";
import { CreateBoxModalLoader } from "./CreateDevboxModalLoader";
import { DisconnectedModal } from "./DisconnectedModal";
import { EditorLayout } from "./EditorLayout";
import { InitBrowserPitcher } from "./InitBrowserPitcher";
import { InitPitcher } from "./InitPitcher";
import { LoadProgressTerminal } from "./LoadProgressTerminal";
import { WebVSCode } from "./WebVSCode";

const vscodeElement = document.createElement("div");
vscodeElement.style.height = "calc(100vh - 44px)";
vscodeElement.style.width = "100vw";

const EditorContent: React.FC<{
  editor: TEditor;
  liveSession?: LiveSession;
}> = ({ editor, liveSession }) => {
  const { sentry } = useEnvironmentInterface();
  const { theme } = useTheme();
  const [vscodeLoadProgress, setVSCodeLoadProgress] =
    useState<VSCodeRemoteLoadProgress>({ type: "idle" });
  const csbApi = useCsbApi();
  const addAlertNotification = useAddAlertNotification();
  const addStatusNotification = useAddStatusNotification();
  const [, dispatchContainer] = useContainer();
  const { open } = useConvertToRepositoryDialog();

  const pitcher = usePitcher();
  const followChannel = useFollowChannel();
  const [session] = useSession();
  const track = useAnalytics();
  const router = useEditorRouter();

  const [hasLoadedVSCode, setHasLoadedVSCode] = useState(false);

  useVSCodeExtensionInitializedEffect(() => {
    setHasLoadedVSCode(true);
  }, []);

  const cancellationTokenSource = useRef(new CancellationTokenSource());
  const remoteOpts = getRemoteOpts(
    pitcher,
    (progress: VSCodeRemoteLoadProgress) => {
      setVSCodeLoadProgress(progress);
    },
    cancellationTokenSource.current.token,
  );

  useControls(
    "Internal.logs",
    {
      client: button(() =>
        window.open(
          "https://sentry.io/organizations/codesandbox/issues/?project=5900707",
        ),
      ),
      pitcher: button(() => {
        window.open(`https://captain.csbops.io/vms/${editor.id}`);
      }),
      "pitcher-manager": button(() =>
        window.open(
          `https://logs2.csbops.io/search/6103ccb49f550a42186632ee?q=cluster%3Av2-beta+AND+namespace_name%3Apitcher-${env.PUBLIC_ENVIRONMENT}+AND+label-app%3Apitcher+AND+label-component%3Amanager&rangetype=relative&relative=900`,
        ),
      ),
      showProtocolMessage: {
        value: false,
        onChange: (value) => {
          /* eslint-disable @typescript-eslint/no-explicit-any */
          (window as any).__CSB__SHOW_PITCHER_MESSAGES = value;
          /* eslint-enable @typescript-eslint/no-explicit-any */
        },
      },
      showVSCodeLogs: {
        value: safeLocalStorage.get("CSB/DEBUG_VSCODE") === "true",
        onChange: (value) => {
          safeLocalStorage.set("CSB/DEBUG_VSCODE", value);
        },
      },
    },
    { collapsed: true },
  );

  useControls(
    "Internal.pitcher",
    {
      "Connect DEV branch": button(() => {
        let url = window.prompt("Insert Pitcher branch connection url");

        if (url) {
          url = url.endsWith("/") ? url.slice(0, url.length - 1) : url;
          setDevPitcherManagerURL(url);
          dispatchContainer({
            type: "RESTART_CONTAINER",
            isSwitchingToDev: true,
          });
        }
      }),
      Hibernate: button(() => {
        pitcher.clients.system.hibernate();
      }),
    },
    { collapsed: true },
    [pitcher],
  );

  useEffect(() => {
    if (
      editor.type === "sandbox" &&
      editor.isCloud &&
      router.queryParams.createRepo.get()
    ) {
      open();
      track("create_repo_from_template", {
        id: editor.id,
        title: editor.title,
        location: "query-param",
      });
    }
  }, []);

  useRegisterConfigFilesCommands();
  useRegisterIntegrationCommands();

  usePitcherNotifications();

  return (
    <EditorStoreProvider
      addAlertNotification={addAlertNotification}
      addStatusNotification={addStatusNotification}
      api={csbApi}
      editor={editor}
      followChannel={followChannel}
      liveSessionInitialState={liveSession}
      pitcher={pitcher}
      sentry={sentry}
      session={session}
      track={track}
    >
      <MonacoLibraryFeature
        assetPaths={{
          staticModulesPath: withBasePath(env, "/static_modules"),
          webWorkersPath: withBasePath(env, "/workers"),
          textMateLanguagesPath: withBasePath(env, "/textmate-languages"),
        }}
        initialTheme={theme.name}
        vscodeLoaderOpts={{
          viewOpts: {
            element: vscodeElement,
          },
          remoteOpts,
        }}
      >
        <ErrorBoundary
          fallback={({ error }) => {
            // eslint-disable-next-line
            console.error(error);
            return (
              <GlobalError
                context={{ error: error.message, originalError: error }}
                errorCode="RUNTIME_ERROR"
              />
            );
          }}
        >
          <SettingsProvider>
            <GlobalModalProvider>
              <ProjectSettingsModal />

              {vscodeLoadProgress.type === "preparing" ? (
                <VSCodeRemoteLoader
                  cancellationTokenSource={cancellationTokenSource.current}
                  vscodeLoadProgress={vscodeLoadProgress}
                />
              ) : (
                <WebVSCode
                  hasLoadedVSCode={hasLoadedVSCode}
                  vscodeElement={vscodeElement}
                />
              )}

              <CodeiumIntegration />
              <MissingEnvVarNotification />
              <AutoOpenTestRunnerDevtool />
            </GlobalModalProvider>
          </SettingsProvider>

          <GlobalNotifications />
        </ErrorBoundary>
      </MonacoLibraryFeature>
    </EditorStoreProvider>
  );
};

const EditorLayoutProvider: React.FC<{
  editor: TEditor;
  container: PickState<
    ContainerState,
    | "READY"
    | "DISCONNECTED"
    | "RECONNECTING"
    | "DISCONNECTED_RECONNECTING"
    | "RESTARTING"
  >;
  liveSession?: LiveSession;
}> = ({ editor, liveSession }) => {
  const router = useEditorRouter();

  return (
    <useActiveFile.Provider router={router}>
      {/* 
            Be aware that moving `GlobalModalProvider` provider might cause issues.
            The modal content could depend on other providers above.
          */}
      <GlobalModalProvider>
        <EditorContent editor={editor} liveSession={liveSession} />
      </GlobalModalProvider>
    </useActiveFile.Provider>
  );
};

export const Editor: React.FC<{ liveSession?: LiveSession }> = ({
  liveSession,
}) => {
  const [session] = useSession();
  const [editorFeature, editorDispatch] = useEditorFeature();
  const { stopBranchingAnimation } = useBranchingAnimation();
  const router = useEditorRouter();
  const track = useAnalytics();
  const isAuthenticated = session.state === "AUTHENTICATED";

  useEffect(() => {
    if (editorFeature.state === "READY") {
      track("editor_loaded", {
        type: editorFeature.editor.type,
        isCloud:
          editorFeature.editor.type === "sandbox"
            ? editorFeature.editor.isCloud
            : true,
      });
    }
  }, [editorFeature]);

  const shouldRenderForkProvider =
    editorFeature.state !== "FETCHING" && editorFeature.state !== "ERROR";

  const ForkProvider = useMemo(() => {
    if (!shouldRenderForkProvider) {
      return () => <></>;
    }

    const editor = editorFeature.editor;

    return ({ children }: React.PropsWithChildren) => {
      return editor.type === "branch" ? (
        <useBranch.Provider
          authorization={editor.authorization}
          defaultBranchName={editor.defaultBranchName}
          onBranchCreateError={() => {
            stopBranchingAnimation();
          }}
          onBranchCreateSuccess={(newBranchName) => {
            editorDispatch({
              type: "SWITCH_BRANCH",
              name: newBranchName,
            });
          }}
          onFork={({ owner, repo, branch, workspaceId }) => {
            router.branch.goToBranch({
              owner,
              repo,
              branch,
              workspaceId,
            });
          }}
          owner={editor.owner}
          repo={editor.repo}
          workspaceId={editor.workspace?.id ?? null}
        >
          {children}
        </useBranch.Provider>
      ) : (
        <useForkSandbox.Provider
          id={editor.id}
          isCloud={editor.isCloud}
          onFork={({ alias, workspaceId }) => {
            // When forking a Devbox and moving to a different workspaceId we
            // want to refresh the browser as you are going to a new VM where
            // VSCode is now out of sync
            if (editor.isCloud && workspaceId !== editor.workspace?.id) {
              window.location.href = `${router.instance.url("devbox", {
                id: alias,
              })}?workspaceId=${workspaceId}`;
            } else {
              editorDispatch({
                type: "SWITCH_SANDBOX",
                alias,
                workspaceId,
              });
            }
          }}
        >
          {children}
        </useForkSandbox.Provider>
      );
    };
  }, [shouldRenderForkProvider]);

  const renderEditor = (editor: TEditor) => {
    const PitcherInitializer =
      window.location.search.includes("forceBrowser=true") ||
      (editor.type === "sandbox" && !editor.isCloud)
        ? InitBrowserPitcher
        : InitPitcher;

    // If there's a initial live session value,
    // we can assume this session is a guest
    const liveSessionRole = liveSession ? "GUEST" : "HOST";

    return (
      <ForkProvider>
        <CreateBoxModalLoader />

        <PitcherInitializer
          id={editor.id}
          seamlessFork={liveSessionRole === "HOST" && canSeamlessBranch(editor)}
        >
          <ContainerProvider branchId={editor.id}>
            <ContainerConsumer>
              {([container]) => {
                return match(container, {
                  RESTARTING: (container) => (
                    <>
                      <EditorLayoutProvider
                        container={container}
                        editor={editor}
                        liveSession={liveSession}
                      />
                    </>
                  ),
                  ERROR: ({ context, error }) => (
                    <ContainerError
                      branchId={editor.id}
                      context={context}
                      error={error}
                    />
                  ),

                  // We only show the disconnected modal if the disconnect was not clean, meaning
                  // it was an unexpected disconnect where a reconnect was not successful
                  DISCONNECTED: (container) => (
                    <>
                      <EditorLayoutProvider
                        container={container}
                        editor={editor}
                        liveSession={liveSession}
                      />
                      {container.wasCleanDisconnect ? null : (
                        <DisconnectedModal
                          wasNotFound={container.wasNotFound}
                        />
                      )}
                    </>
                  ),
                  DISCONNECTED_RECONNECTING: (container) => (
                    <>
                      <EditorLayoutProvider
                        container={container}
                        editor={editor}
                        liveSession={liveSession}
                      />
                    </>
                  ),
                  READY: (container) => (
                    <>
                      <EditorLayoutProvider
                        container={container}
                        editor={editor}
                        liveSession={liveSession}
                      />
                    </>
                  ),
                  RECONNECTING: (container) => (
                    <>
                      <EditorLayoutProvider
                        container={container}
                        editor={editor}
                        liveSession={liveSession}
                      />
                    </>
                  ),
                });
              }}
            </ContainerConsumer>
          </ContainerProvider>
        </PitcherInitializer>
      </ForkProvider>
    );
  };

  return (
    <AnimatePresence>
      {match(editorFeature, {
        READY: ({ editor }) => renderEditor(editor),
        RENAMING_BRANCH: ({ editor }) => renderEditor(editor),
        REMOVING_BRANCH: ({ editor }) => renderEditor(editor),
        FETCHING: () => (
          <EditorLayout>
            <BlockingLoader
              loadingBarEnd={20}
              loadingBarProgress={0}
              loadingBarSpeed={6}
              loadingBarStart={0}
            >
              {router.branch.queryParams.create.get()
                ? "Creating branch..."
                : "Fetching details..."}
            </BlockingLoader>
          </EditorLayout>
        ),
        ERROR: ({
          error,
          couldNotAccessWorkspace,
          possibleWorkspacesToRequestAccess,
        }) => {
          let errorCode: KnownErrors = "UNKNOWN_ERROR";

          const isSandbox = router.type === "sandbox";
          const isDevbox = router.type === "devbox";

          if (
            couldNotAccessWorkspace &&
            possibleWorkspacesToRequestAccess &&
            possibleWorkspacesToRequestAccess.length > 0
          ) {
            errorCode = "REQUEST_WORKSPACE_ACCESS";
          } else if (
            couldNotAccessWorkspace ||
            error.includes("log in") ||
            error.includes("not authenticated") ||
            error.includes("not authorized")
          ) {
            errorCode = "NO_WORKSPACE_ACCESS";
          } else if (!isAuthenticated) {
            errorCode = "UNAUTHENTICATED";
          } else if (
            (isSandbox && error.includes("Error: (404) Not found")) ||
            error.includes(`"Sandbox not found"`) ||
            (error === "NOT_FOUND" && isSandbox) ||
            (error.includes("NOT_FOUND") && isSandbox)
          ) {
            errorCode = "SANDBOX_NOT_FOUND";
          } else if (isDevbox && error.includes("Error: (404) Not found")) {
            errorCode = "DEVBOX_NOT_FOUND";
          } else if (error.includes(`"Repository or team not found"`)) {
            errorCode = "RESTRICTED_REPOSITORY";
          } else if (
            /**
             * @TODO API should also return error codes, instead of messages
             */
            error.includes(`"Repository not found"`) ||
            error.includes(`"Repository or branch not found"`) ||
            error.includes(`"Project or branch not found"`) ||
            error === "NOT_FOUND"
          ) {
            errorCode = "REPOSITORY_NOT_FOUND";
          } else if (error.includes(`Branch not found`)) {
            errorCode = "BRANCH_NOT_FOUND";
          }

          return (
            <GlobalError
              context={{ error }}
              errorCode={errorCode}
              possibleWorkspacesToRequestAccess={
                possibleWorkspacesToRequestAccess
              }
            />
          );
        },
      })}
    </AnimatePresence>
  );
};

const VSCodeRemoteLoader: React.FC<{
  vscodeLoadProgress: VSCodeRemoteLoadProgress;
  cancellationTokenSource: CancellationTokenSource;
}> = ({ vscodeLoadProgress, cancellationTokenSource }) => {
  const [cancelling, setCancelling] = useState(false);

  if (vscodeLoadProgress.type !== "preparing") {
    return null;
  }

  return (
    <EditorLayout>
      <BlockingLoader
        key={vscodeLoadProgress.message}
        loadingBarEnd={100}
        loadingBarProgress={75}
        loadingBarSpeed={4}
        loadingBarStart={75}
      >
        <span>{vscodeLoadProgress.message}</span>

        {vscodeLoadProgress.onDidOutput && (
          <Box css={{ width: 0, margin: "auto" }}>
            <Box
              css={{
                marginTop: "$8",
                width: "768px",
                transform: "translateX(-50%)",
              }}
              selectable
            >
              <LoadProgressTerminal onOutput={vscodeLoadProgress.onDidOutput} />
              <Button
                css={{ marginTop: "$2" }}
                onClick={() => {
                  setCancelling(true);
                  cancellationTokenSource.cancel();
                }}
              >
                {cancelling
                  ? "Cancelling initialization..."
                  : "Skip VS Code initialization"}
              </Button>
            </Box>
          </Box>
        )}
      </BlockingLoader>
    </EditorLayout>
  );
};

function getRemoteOpts(
  pitcher: IPitcherClient,
  onProgress: RemoteOpts["onProgress"],
  cancellationToken: CancellationToken,
): RemoteOpts | undefined {
  try {
    const parsedUrl = new URL(document.location.href);

    if (
      parsedUrl.searchParams.has("remote") ||
      getExperiments().serverExtensions
    ) {
      const canCreateTerminals = Boolean(
        pitcher.capabilities.shell?.createTerminate,
      );
      const hasPermissions = Boolean(pitcher.permissions.shell?.create);
      const isProtected = Boolean(pitcher.permissions.fs?.operation) === false;

      if (canCreateTerminals && hasPermissions) {
        return { isProtected, onProgress, cancellationToken };
      }
    }
  } catch (e) {
    return undefined;
  }

  return undefined;
}
